import { h } from 'preact';
import Store from 'store/store';
import { debounce, joinClasses } from 'utils/utils';
import { canUser } from 'utils/roles';
import * as jsonPointer from 'utils/jsonPointer';

import Button from 'components/button/button';
import FieldBase from 'components/editor/settings/fieldBase';

import s from 'components/editor/settings/showtimesField.sss';

// TODO: move to utils?
const moviesCache = new Map();
async function moviesRequest(qs) {
	const url = new URL('/v2.0/movies', 'https://showtimes-v2.s-prod.pow.io/');
	Object.entries(qs).forEach(([k, v]) => url.searchParams.set(k, v));
	const res = await fetch(url);
	if (!res.ok) throw new Error(res.status);
	const data = await res.json();
	data.response.forEach(m => moviesCache.set(m.id, m));
	return data.response;
}

async function searchMovies(value) {
	if (!value?.length) return [];
	return await moviesRequest({ search: value });
}

async function getMovie(id) {
	if (!moviesCache.has(id)) {
		moviesCache.set(id, moviesRequest({ id }).then(r => r[0]));
	}
	return await moviesCache.get(id);
}

export default class ShowtimesField extends FieldBase {
	get disabled() {
		return super.disabled || (this.props.path && !canUser('write'));
	}

	constructor(props) {
		super(props);

		this.setState({
			selectedMovies: [],
			inheritedMovies: [],
			loading: 'loaded',
			search: '',
			results: [],
		});
		this.searchId = 0;

		this.select = this.select.bind(this);
		this.clear = this.clear.bind(this);
		this.onSearchInput = this.onSearchInput.bind(this);
		this.debouncedSearch = debounce(this.search.bind(this), 500);
	}

	componentDidMount() {
		this.updateSelectedMovies();
	}

	componentWillReceiveProps(nextProps) {
		this.updateSelectedMovies(nextProps);
	}

	async updateSelectedMovies(props = this.props) {
		try {
			if (this.canInherit) {
				const baseValue = this.getParsedInheritedValue(props);
				const inheritedMovies = await Promise.all(baseValue.map(getMovie));
				if (inheritedMovies.map(m => m.id).sort().join(',') !== this.state.inheritedMovies.map(m => m.id).sort().join(',')) {
					this.setState({ inheritedMovies });
				}
			}

			const value = this.getParsedValue(props);
			if (!value.length) {
				this.setState({ selectedMovies: [] });
				if (!this.state.search) {
					this.setState({ results: [] });
				}
				return;
			}

			const movies = await Promise.all(value.map(getMovie));
			if (movies.map(m => m.id).sort().join(',') !== this.state.selectedMovies.map(m => m.id).sort().join(',')) {
				this.setState({ selectedMovies: movies });
				if (!this.state.search) {
					this.setState({ results: movies });
				}
			}
		} catch {}
	}

	getParsedValue(props = this.props) {
		const value = this.getValue(props);
		if (!value || typeof value !== 'string') return [];
		return value.split(',');
	}

	getParsedInheritedValue(props = this.props) {
		const value = this.getInheritedValue(props);
		if (!value || typeof value !== 'string') return [];
		return value.split(',');
	}

	select(e) {
		const multiSelect = e.shiftKey;
		let value = e.currentTarget.dataset.value;
		if (multiSelect) {
			const all = this.getParsedValue();
			let updated = all.includes(value) ? all.filter(v => v !== value) : [...all, value];
			value = updated.join(',');
		}
		this.props.onChange?.({ target: { value } });
		if (this.props.path) {
			jsonPointer.setDeep(Store.get().data, this.props.path, value);
		}
		if (!multiSelect) {
			e.currentTarget.closest('[popover]')?.hidePopover();
		}
	}

	async search(value) {
		const searchId = ++this.searchId;
		if (!value?.length) {
			return this.setState({ loading: 'loaded', search: value, results: this.state.selectedMovies });
		}
		this.setState({ loading: 'loading' });
		try {
			const results = await searchMovies(value);
			if (searchId !== this.searchId) {
				return;
			}
			this.setState({ loading: 'loaded', results, search: value });
		} catch (e) {
			if (searchId !== this.searchId) {
				return;
			}
			this.setState({ loading: 'error', search: value });
		}
	}

	onSearchInput(e) {
		this.setState({ loading: 'loading' });
		this.debouncedSearch(e.target.value);
	}

	clear(e) {
		e.stopPropagation();
		e.preventDefault();
		this.props.onChange?.({ target: { value: null } });
		if (this.props.path) {
			jsonPointer.setDeep(Store.get().data, this.props.path, null);
		}
	}

	renderOptions() {
		const state = this.state;
		if (state.loading === 'loading') {
			return <div class={s.loading} key="loading">Loading...</div>;
		}
		if (state.loading === 'error') {
			return <div class={s.error} key="error">Sorry, an error occured</div>;
		}
		const activeValue = this.getParsedValue();
		return state.results.map(result => {
			const showCheck = activeValue.includes(result.id);
			const releaseYear = result.release_date?.split('-')[0] || 'In Production';
			const directorLabel = [releaseYear].concat(result.director?.split(',').map(e => e.trim())).filter(Boolean).join(', ');
			const screeningsLabel = `${result.screenings.toLocaleString(undefined, { notation: 'compact' })} screening${result.screenings !== 1 ? 's' : ''}`;
			return (
				<Button class={s.movieOption} iconAlt={showCheck ? 'check' : 'none'} iconSize="small" data-value={result.id} onClick={this.select} key={result.id}>
					<div class={s.movieName}>
						{result.title}
						<span class={s.director}>{directorLabel}</span>
					</div>
					<div class={s.screenings}>{screeningsLabel}</div>
				</Button>
			);
		});
	}

	renderField() {
		const name = this.props.name || 'showtimes';
		const popoverId = `${name}-popover`;
		const hasValue = !!this.getParsedValue()?.length;
		const label = (
			this.isInheriting
				? this.state.inheritedMovies.map(movie => movie?.title).join(', ')
				: this.state.selectedMovies.map(movie => movie?.title).join(', ')
		) || 'Select a movie...';
		const showClear = this.props.allowClear && hasValue;

		return (
			<Button
				id={this.state.id}
				class={s.field}
				type="button"
				popovertarget={popoverId}
				icon={this.props.icon}
				iconAlt="chevron-down"
				iconSize="small"
				dark={this.props.dark}
				disabled={this.disabled}
				readonly={this.readonly}
			>
				<div class={joinClasses(s.label, !hasValue && s.empty)}>{label}</div>
				{showClear && <Button class={s.clear} icon="cancel" dark={this.props.dark} disabled={this.disabled} onClick={this.clear} />}
				<div popover="auto" id={popoverId}>
					<input type="text" class={s.search} placeholder="Search movies..." onInput={this.onSearchInput} autofocus />
					<hr />
					<div class={s.list} data-search={this.state.search}>
						{this.renderOptions()}
					</div>
				</div>
			</Button>
		);
	}
}
